// Preview dial mode — compact agent bar.
// Shows when the agent's active campaign is mode=preview. The agent pulls one
// lead, then Dials or Skips. The DIAL routes through the normal softphone, so
// hangup, disposition (standard wrap-up), transfer and conference all behave
// exactly like any other call. When the wrap-up completes (`dialer:ended`),
// the lead is advanced and the bar resets so the next lead can be pulled.
import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, SkipForward, Loader2, UserSquare2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function PreviewDialPanel() {
  const [campaign, setCampaign] = useState(null);
  const [lead, setLead] = useState(null);
  const [phase, setPhase] = useState("idle");   // idle | got | dialed
  const [busy, setBusy] = useState(false);
  const stateRef = useRef({ campaign: null, lead: null, phase: "idle" });
  stateRef.current = { campaign, lead, phase };

  const resolveCampaign = useCallback(async () => {
    try {
      const r = await api.get("/me/campaigns/active");
      const prev = (r.data || []).find((c) => (c.data?.mode || "") === "preview");
      setCampaign(prev || null);
      if (!prev) { setLead(null); setPhase("idle"); }
    } catch { setCampaign(null); }
  }, []);

  const advanceAndReset = useCallback(async (outcome, dispositionId) => {
    const { campaign: c, lead: l } = stateRef.current;
    if (c && l) {
      const body = { lead_id: l.id, outcome };
      if (dispositionId) body.disposition_id = dispositionId;
      try { await api.post(`/dialer/campaigns/${c.id}/disposition`, body); } catch {}
    }
    setLead(null); setPhase("idle");
  }, []);

  useEffect(() => {
    resolveCampaign();
    const onChanged = () => resolveCampaign();
    // When the standard wrap-up finishes, advance the preview lead using the
    // disposition the agent picked in the wrap-up dialog (carried on the
    // event), so the lead reflects the real disposition code — not "done".
    const onEnded = (e) => {
      if (stateRef.current.phase === "dialed") {
        advanceAndReset("answered", e?.detail?.disposition || null);
      }
    };
    window.addEventListener("c7:campaign-changed", onChanged);
    window.addEventListener("dialer:ended", onEnded);
    return () => {
      window.removeEventListener("c7:campaign-changed", onChanged);
      window.removeEventListener("dialer:ended", onEnded);
    };
  }, [resolveCampaign, advanceAndReset]);

  async function getNext() {
    if (!campaign) return;
    setBusy(true);
    try {
      const r = await api.post(`/dialer/campaigns/${campaign.id}/next-lead`);
      setLead(r.data.lead); setPhase("got");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) toast.message("No eligible leads right now.");
      else if (code === 409) toast.error("This preview campaign isn't running. Ask your admin to Start it.");
      else toast.error(formatErr(e));
      setLead(null); setPhase("idle");
    } finally { setBusy(false); }
  }

  async function dial() {
    if (!campaign || !lead?.phone) return;
    setBusy(true);
    try {
      // Mark the held lead as dialing ONLY now (agent clicked Dial).
      await api.post(`/dialer/campaigns/${campaign.id}/dial-lead`, { lead_id: lead.id });
      window.dispatchEvent(new CustomEvent("c7:dial-number", { detail: { number: lead.phone } }));
      setPhase("dialed");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  async function skip() {
    if (!campaign || !lead) return;
    setBusy(true);
    try { await advanceAndReset("skipped"); await getNext(); }
    finally { setBusy(false); }
  }

  if (!campaign) return null;

  return (
    <div
      data-testid="preview-dial-panel"
      className="flex items-center gap-3 rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30 px-3 py-2"
    >
      <UserSquare2 className="w-4 h-4 text-blue-700 dark:text-blue-300 shrink-0" />
      <span className="text-sm font-semibold text-blue-900 dark:text-blue-200 shrink-0">Preview · {campaign.name}</span>
      <Badge variant="outline" className="text-[10px] uppercase shrink-0">{phase === "dialed" ? "On call" : phase === "got" ? "Lead ready" : "Idle"}</Badge>

      {lead && (
        <div className="flex items-center gap-2 min-w-0 text-sm">
          <span className="font-semibold truncate" data-testid="preview-lead-name">{lead.name || "Unnamed lead"}</span>
          <span className="font-mono text-muted-foreground truncate" data-testid="preview-lead-phone">{lead.phone}</span>
          <span className="text-[11px] text-muted-foreground shrink-0">· Attempt {lead.attempts || 1}</span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-1.5 shrink-0">
        {phase === "idle" && (
          <Button data-testid="preview-get-lead" size="sm" onClick={getNext} disabled={busy} className="gap-1.5 h-8">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />} Get next lead
          </Button>
        )}
        {phase === "got" && (
          <>
            <Button data-testid="preview-skip" size="sm" variant="outline" onClick={skip} disabled={busy} className="gap-1.5 h-8"><SkipForward className="w-3.5 h-3.5" /> Skip</Button>
            <Button data-testid="preview-dial" size="sm" onClick={dial} disabled={busy} className="gap-1.5 h-8 bg-emerald-600 hover:bg-emerald-700 text-white"><Phone className="w-3.5 h-3.5" /> Dial</Button>
          </>
        )}
        {phase === "dialed" && (
          <span className="text-[11px] text-muted-foreground">Hang up &amp; disposition from the call controls →</span>
        )}
      </div>
    </div>
  );
}
